import { BorderRadius, Color, Spacing, Typography } from '@persona-id/design-tokens';
import { ArrowLeftIcon, ArrowRightIcon } from '@persona-id/design-tokens/icons/24px';
import { useState } from 'react';
import { Link } from 'react-router-dom-v5-compat';
import styled, { css } from 'styled-components';

import { ModalBody, ModalHeader } from 'dashboard/layouts/ModalLayout';
import {
  IndustryName,
  industryToUseCasesMap,
  useCasesV2Catalog,
  UseCaseV2Name,
} from 'dashboard/models/blueprint';
import {
  ModalV2Footer,
  ModalV2Header,
  ModalV2Subheader,
} from 'dashboard/views/DashboardSandboxHomeView/BlueprintsQuizV2Modal/BlueprintsQuizV2ModalLayout';
import Button from 'design-system/components/Button';
import Spacer from 'design-system/components/Spacer';
import { Paragraph } from 'design-system/components/Text';

interface BlueprintsQuizV2UseCasesProps {
  selectedIndustry: IndustryName;
  selectedUseCase: UseCaseV2Name;
  toPreviousScreen: () => void;
  toNextScreen: () => void;
  submit: (selectedOption: UseCaseV2Name) => void;
  closeModal: () => void;
}

export const BlueprintsQuizV2UseCases = ({
  selectedIndustry,
  selectedUseCase,
  toPreviousScreen,
  toNextScreen,
  submit,
  closeModal,
}: BlueprintsQuizV2UseCasesProps) => {
  const [selectedOption, setSelectedOption] = useState(selectedUseCase);

  const handleNextScreen = () => {
    submit(selectedOption);
    toNextScreen();
  };

  let useCaseOptions = industryToUseCasesMap[selectedIndustry];
  const hasKybUseCase =
    useCaseOptions.includes(UseCaseV2Name.Kyb) ||
    useCaseOptions.includes(UseCaseV2Name.OnboardBusinesses);
  // Filter out KYB use case for now, as we don't have a self-serve KYB Blueprint
  useCaseOptions = useCaseOptions.filter(
    (useCase) => useCase !== UseCaseV2Name.Kyb && useCase !== UseCaseV2Name.OnboardBusinesses,
  );

  return (
    <>
      <ModalHeader onClose={closeModal}>
        <ModalV2Subheader>Step 2 of 3</ModalV2Subheader>
        <ModalV2Header>What type of challenge are you hoping to solve with Persona?</ModalV2Header>
      </ModalHeader>
      <ModalBody>
        <UseCaseOptionsWrapper>
          {useCaseOptions.map((useCaseName) => (
            <UseCaseOption
              key={`use-case-option-${useCaseName}`}
              isSelected={selectedOption === useCaseName}
              onClick={() => setSelectedOption(useCaseName)}
            >
              {useCasesV2Catalog[useCaseName].icon} {useCasesV2Catalog[useCaseName].label}
            </UseCaseOption>
          ))}
        </UseCaseOptionsWrapper>
        {hasKybUseCase && (
          <>
            <Spacer height={2} />
            <Paragraph color='secondary'>
              Persona also offers Know Your Business (KYB) solutions for Growth and Enterprise
              customers. Please <Link to='/dashboard/contact-us'>contact us</Link> if you’d like to
              learn more about these solutions.
            </Paragraph>
          </>
        )}
      </ModalBody>
      <ModalV2Footer>
        <Button
          aria-label='blueprints-quiz-v2-use-case__button-back'
          width='auto'
          variant='basic'
          type='button'
          onClick={toPreviousScreen}
          prefixIcon={<ArrowLeftIcon />}
        >
          Back
        </Button>
        <Button
          aria-label='blueprints-quiz-v2-use-case__button-next'
          width='auto'
          type='button'
          onClick={handleNextScreen}
          disabled={!selectedOption}
          suffixIcon={<ArrowRightIcon />}
        >
          Next
        </Button>
      </ModalV2Footer>
    </>
  );
};

const UseCaseOptionsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  column-gap: ${Spacing[4]};
  row-gap: ${Spacing[4]};
`;

const UseCaseOption = styled.div<{ isSelected: boolean }>`
  ${Typography.TextMedium};
  padding: ${Spacing[2]} ${Spacing[4]};
  border: 1px solid ${Color.BorderDivider};
  border-radius: ${BorderRadius.Md};
  min-width: 300px;
  cursor: pointer;

  :first-letter {
    margin-right: ${Spacing[1]};
  }

  ${({ isSelected }) =>
    isSelected &&
    css`
      background-color: ${Color.CorePurple100};
    `}
`;
